"use client";

import { useState, useEffect } from "react";
import dayjs from "@punchup/utils/dayjs";
import { ShowWithComedian } from "@/data/shows";
import { Comedian } from "@/data/comedians";
import { trackEvent, UserEvents } from "@/analytics";
import { getMixpanelDistinctId } from "@punchup/analytics";
import {
  convertToAffiliate,
  isTicketmasterUrl,
} from "@/utils/ticketmasterConverter";
import { useGate } from "statsig-react";
import { getCDNImageURL } from "@/data/images";
import { useTixologiWidget } from "@/hooks/tixologi/widget";
import { useRouter } from "next/navigation";
import { toast } from "@/shadcn/ui/use-toast";
import {
  AutoJoinWaitlist,
  MultipleComediansDrawer,
} from "@punchup/venues/client";
import { isTixologiCheckoutShow } from "@punchup/tixologi/widget-origin";

import EventPageHero from "./EventPageHero";
import EventAboutSection from "./EventAboutSection";
import EventTicketCard from "./EventTicketCard";
import EventPageCarousels from "./EventPageCarousels";

interface EventPageClientProps {
  show: ShowWithComedian;
  comedian: Comedian;
  authenticated: boolean;
  userFollowsComedian: boolean;
  tixologiData?: any;
  /** Server-sanitized show description HTML (PUN-5353) — the sanitizer is
   *  server-only (cheerio), so the server page sanitizes and passes it down. */
  sanitizedDescriptionHtml?: string;
  sanitizedTixologiDescriptionHtml?: string;
  allComedians?: Comedian[]; // For multi-comic shows
  isPunchupComedian?: boolean;
  followedComedianIds?: string[];
  venueSlug?: string;
}

export default function EventPageClient({
  show,
  comedian,
  authenticated,
  userFollowsComedian,
  tixologiData,
  sanitizedDescriptionHtml,
  sanitizedTixologiDescriptionHtml,
  allComedians = [],
  isPunchupComedian = false,
  followedComedianIds = [],
  venueSlug,
}: EventPageClientProps) {
  const [isFollowing, setIsFollowing] = useState(userFollowsComedian);
  const [isComediansDrawerOpen, setIsComediansDrawerOpen] = useState(false);
  const { open: openTixologiWidget } = useTixologiWidget();
  const router = useRouter();
  const { value: ticketmasterAffiliateEnabled } = useGate(
    "ticketmaster_affiliate_converter"
  );

  // Computed values
  // Parse the datetime - it's stored as UTC but should be displayed in local time
  const eventDate = dayjs(show.datetime);
  const isUpcoming = eventDate.isAfter(dayjs());
  const isSoldOut = show.is_sold_out || false;
  const isTixologiShow = isTixologiCheckoutShow(
    show.ticket_link,
    show.tixologi_event_id
  );
  const isMultiComic = allComedians.length > 1;
  const isHeatingUp = show.flags?.includes("LOW_TICKETS") || false;
  const isLowTickets = show.flags?.includes("LOW_TICKETS") || false;
  // Today the only signal is `is_sold_out`. When a dedicated HAS_WAITLIST
  // ticket_flags value lands, promote this to include flag-opted shows.
  const hasWaitlist = isSoldOut;

  // Image handling
  const rawPosterImage = tixologiData?.poster_img || show.poster_img;
  const posterImage = rawPosterImage
    ? rawPosterImage.startsWith("http")
      ? rawPosterImage
      : getCDNImageURL(rawPosterImage)
    : null;
  const heroImage = comedian.about_img
    ? comedian.about_img.startsWith("http")
      ? comedian.about_img
      : getCDNImageURL(comedian.about_img)
    : null;

  // Convert Ticketmaster links to affiliate links (same gate as the nearby
  // shows surface), attributing this surface as subId1 = "event-page"
  const ticketLink =
    show.ticket_link &&
    ticketmasterAffiliateEnabled &&
    isTicketmasterUrl(show.ticket_link)
      ? convertToAffiliate(show.ticket_link, {
          src: "event-page",
          distinctId: getMixpanelDistinctId(),
        })
      : show.ticket_link;

  const vipTicketLink =
    show.vip_ticket_link &&
    ticketmasterAffiliateEnabled &&
    isTicketmasterUrl(show.vip_ticket_link)
      ? convertToAffiliate(show.vip_ticket_link, {
          src: "event-page",
          distinctId: getMixpanelDistinctId(),
        })
      : show.vip_ticket_link;

  // Event handlers

  const handleTicketClick = () => {
    trackEvent(UserEvents.BUY_TICKETS_CLICKED, {
      ShowId: show.id,
      ComedianId: comedian.id,
      ComedianName: comedian.display_name,
      Source: "event_page",
      ShowVenue: show.venue,
      ShowDate: eventDate.toISOString(),
    });

    // If it's a Tixologi show, open the widget instead
    if (isTixologiShow && openTixologiWidget) {
      // Transform ShowWithComedian to ShowInfo format
      const showInfo = {
        id: show.id,
        comedian_id: show.comedian_id,
        datetime: eventDate,
        datetimeOriginal: show.datetime,
        location: show.location,
        venue: show.venue,
        ticketLink: show.ticket_link || "",
        venueId: show.venue_id || "",
        soldOut: show.is_sold_out || false,
        metadataText: show.metadata_text || "",
        vipTicketLink: show.vip_ticket_link || "",
        presaleCode: show.presale_code || "",
        flags: show.flags || [],
        title: show.title || undefined,
        tixologiEventId: show.tixologi_event_id,
        posterImg: show.poster_img || null,
      };
      openTixologiWidget(showInfo);
    } else if (ticketLink) {
      window.open(ticketLink, "_blank");
    }
  };

  const handleVIPTicketClick = () => {
    trackEvent(UserEvents.BUY_TICKETS_CLICKED, {
      ShowId: show.id,
      ComedianId: comedian.id,
      ComedianName: comedian.display_name,
      Source: "event_page_vip",
      ShowVenue: show.venue,
      ShowDate: eventDate.toISOString(),
    });

    if (vipTicketLink) {
      window.open(vipTicketLink, "_blank");
    }
  };

  // Track page view
  useEffect(() => {
    trackEvent(UserEvents.LOADED_CONTENT_PAGE, {
      event_id: show.id,
      comedian_id: comedian.id,
      comedian_name: comedian.display_name,
      venue: show.venue,
      location: show.location,
      is_upcoming: isUpcoming,
      page: "event_page",
    });
  }, [
    show.id,
    comedian.id,
    comedian.display_name,
    show.venue,
    show.location,
    isUpcoming,
  ]);

  return (
    <div className="min-h-screen bg-gray-50">
      <AutoJoinWaitlist />
      {/* Hero Section */}
      <EventPageHero
        show={show}
        comedian={comedian}
        heroImage={heroImage}
        eventDate={eventDate}
        isUpcoming={isUpcoming}
        isSoldOut={isSoldOut}
        hasWaitlist={hasWaitlist}
        isHeatingUp={isHeatingUp}
        isLowTickets={isLowTickets}
        isPunchupComedian={isPunchupComedian}
        isMultiComic={isMultiComic}
        venueSlug={venueSlug}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - About Sections */}
          <EventAboutSection
            show={show}
            comedian={comedian}
            posterImage={posterImage}
            tixologiData={tixologiData}
            sanitizedDescriptionHtml={sanitizedDescriptionHtml}
            sanitizedTixologiDescriptionHtml={sanitizedTixologiDescriptionHtml}
            allComedians={allComedians}
            isMultiComic={isMultiComic}
            isPunchupComedian={isPunchupComedian}
            followedComedianIds={followedComedianIds}
            isFollowing={isFollowing}
            onComediansDrawerOpen={() => setIsComediansDrawerOpen(true)}
          />

          {/* Right Column - Ticket Card */}
          <EventTicketCard
            show={show}
            comedian={comedian}
            eventDate={eventDate}
            isUpcoming={isUpcoming}
            isSoldOut={isSoldOut}
            isTixologiShow={isTixologiShow}
            isPunchupComedian={isPunchupComedian}
            isMultiComic={isMultiComic}
            isFollowing={isFollowing}
            authenticated={authenticated}
            onTicketClick={handleTicketClick}
            onVIPTicketClick={handleVIPTicketClick}
          />
        </div>
      </div>

      {/* Engagement Carousels */}
      <EventPageCarousels authenticated={authenticated} />

      {/* Multiple Comedians Drawer for Showcases */}
      {isMultiComic && (
        <MultipleComediansDrawer
          isOpen={isComediansDrawerOpen}
          setIsOpen={setIsComediansDrawerOpen}
          comedians={allComedians.map((c) => ({
            ...c,
            is_punchup_verified: c.is_punchup_verified || false,
          }))}
          followedComedians={followedComedianIds}
        />
      )}
    </div>
  );
}
